// QR Kiosk v2 — KV2-7 task data slice (createAsyncThunk). Additive: `publicSlice`
// is byte-untouched and this slice imports NOTHING from kioskV2Slice (the surface
// plumbing listens the other way — cycle-safe, same one-way rule as publicSlice).
//
// The ONE deviation from publicSlice's bare thunks: every thunk captures the error
// via rejectWithValue({ status, message }) so the detail page can branch 404 vs 403
// vs generic (§9.7) and the sheets can surface the server `message` (§9.6).
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import kioskTasksApi from "./kioskTasksApi";
import { KioskTask, KioskTaskAction, KioskTaskDetail, KioskTaskPhoto, KioskProgressCreated } from "./kioskTasksModels";

export interface KioskTaskReject {
  status: number;
  message?: string;
}

const toReject = (err: any): KioskTaskReject => ({
  status: err?.response?.status ?? 0,
  message: err?.response?.data?.message,
});

export const getKioskTasks = createAsyncThunk<KioskTask[], { machineId: string }, { rejectValue: KioskTaskReject }>(
  "kioskTasks/getTasks",
  async ({ machineId }, { rejectWithValue }) => {
    try {
      return await kioskTasksApi.getTasks(machineId);
    } catch (err) {
      return rejectWithValue(toReject(err));
    }
  }
);

export const getKioskTask = createAsyncThunk<KioskTaskDetail, { number: string }, { rejectValue: KioskTaskReject }>(
  "kioskTasks/getTask",
  async ({ number }, { rejectWithValue }) => {
    try {
      return await kioskTasksApi.getTaskByNumber(number);
    } catch (err) {
      return rejectWithValue(toReject(err));
    }
  }
);

export const startKioskTask = createAsyncThunk<KioskTaskDetail, { id: string; number: string }, { rejectValue: KioskTaskReject }>(
  "kioskTasks/start",
  async ({ id }, { rejectWithValue }) => {
    try {
      return await kioskTasksApi.startTask(id);
    } catch (err) {
      return rejectWithValue(toReject(err));
    }
  }
);

export const holdKioskTask = createAsyncThunk<KioskTaskDetail, { id: string; number: string; reason: string }, { rejectValue: KioskTaskReject }>(
  "kioskTasks/hold",
  async ({ id, reason }, { rejectWithValue }) => {
    try {
      return await kioskTasksApi.holdTask(id, reason);
    } catch (err) {
      return rejectWithValue(toReject(err));
    }
  }
);

export const resumeKioskTask = createAsyncThunk<KioskTaskDetail, { id: string; number: string }, { rejectValue: KioskTaskReject }>(
  "kioskTasks/resume",
  async ({ id }, { rejectWithValue }) => {
    try {
      return await kioskTasksApi.resumeTask(id);
    } catch (err) {
      return rejectWithValue(toReject(err));
    }
  }
);

export const doneKioskTask = createAsyncThunk<
  KioskTaskDetail,
  { id: string; number: string; completionNote: string; photos: KioskTaskPhoto[] },
  { rejectValue: KioskTaskReject }
>("kioskTasks/done", async ({ id, completionNote, photos }, { rejectWithValue }) => {
  try {
    return await kioskTasksApi.doneTask(id, completionNote, photos);
  } catch (err) {
    return rejectWithValue(toReject(err));
  }
});

export const addKioskTaskNote = createAsyncThunk<
  KioskProgressCreated,
  { id: string; number: string; text: string; photos: KioskTaskPhoto[] },
  { rejectValue: KioskTaskReject }
>("kioskTasks/addNote", async ({ id, text, photos }, { rejectWithValue }) => {
  try {
    return await kioskTasksApi.addNote(id, text, photos);
  } catch (err) {
    return rejectWithValue(toReject(err));
  }
});

export interface KioskTasksState {
  tasks?: KioskTask[]; // list for tasksMachineId
  tasksMachineId?: string; // GUID the list was loaded for (route-scope guard, lowercased)
  task?: KioskTaskDetail; // detail (route-scoped by number at render)
  taskErrorStatus?: number; // last getKioskTask rejection status (404/403 branch)
  acting?: KioskTaskAction | "noteSubmit"; // in-flight action (disables buttons/sheets)
  actionError?: { action: string; message?: string; status: number }; // last action failure
  noteCreated?: string; // ISO stamp of the last successful note (transient confirmation)
}

const initialState: KioskTasksState = {};

// Map a fresh KioskTaskDetail back to its base list fields, patching the loaded list in
// place so back-nav is correct without a refetch (§5.2).
function patchTaskRow(state: KioskTasksState, detail: KioskTaskDetail) {
  if (!state.tasks) return;
  const idx = state.tasks.findIndex((t) => t.id === detail.id);
  if (idx < 0) return;
  const {
    id, number, title, kioskStatus, statusId, pendingReview, priorityCode,
    dueDate, isOverdue, isMine, assigneeName, actualStartDate, actualEndDate,
  } = detail;
  state.tasks[idx] = {
    id, number, title, kioskStatus, statusId, pendingReview, priorityCode,
    dueDate, isOverdue, isMine, assigneeName, actualStartDate, actualEndDate,
  };
}

const kioskTasksSlice = createSlice({
  name: "kioskTasks",
  initialState,
  reducers: {
    clearActionError(state) {
      state.actionError = undefined;
    },
    clearNoteCreated(state) {
      state.noteCreated = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getKioskTasks.fulfilled, (state, action) => {
        state.tasks = action.payload;
        state.tasksMachineId = action.meta.arg.machineId.toLowerCase();
      })
      // Never clear `task` on pending/rejected — the cache backs the KV2-5 dimmed state.
      .addCase(getKioskTask.pending, (state) => {
        state.taskErrorStatus = undefined;
        state.actionError = undefined;
      })
      .addCase(getKioskTask.fulfilled, (state, action) => {
        state.task = action.payload;
      })
      .addCase(getKioskTask.rejected, (state, action) => {
        state.taskErrorStatus = action.payload?.status;
      });

    // The four transition thunks share the same settle shape: fulfilled delivers the
    // fresh detail (task = payload) + patches the list row; rejected records actionError.
    // Written explicitly (not a loop over a union of thunks) to keep addCase strictly typed.
    const onTransitionFulfilled = (state: KioskTasksState, payload: KioskTaskDetail) => {
      state.acting = undefined;
      state.task = payload;
      patchTaskRow(state, payload);
    };
    const setActing = (state: KioskTasksState, action: KioskTaskAction) => {
      state.acting = action;
      state.actionError = undefined;
    };
    const onTransitionRejected = (state: KioskTasksState, action: KioskTaskAction, reject?: KioskTaskReject) => {
      state.acting = undefined;
      state.actionError = { action, message: reject?.message, status: reject?.status ?? 0 };
    };

    builder
      .addCase(startKioskTask.pending, (state) => setActing(state, "start"))
      .addCase(startKioskTask.fulfilled, (state, a) => onTransitionFulfilled(state, a.payload))
      .addCase(startKioskTask.rejected, (state, a) => onTransitionRejected(state, "start", a.payload))
      .addCase(holdKioskTask.pending, (state) => setActing(state, "hold"))
      .addCase(holdKioskTask.fulfilled, (state, a) => onTransitionFulfilled(state, a.payload))
      .addCase(holdKioskTask.rejected, (state, a) => onTransitionRejected(state, "hold", a.payload))
      .addCase(resumeKioskTask.pending, (state) => setActing(state, "resume"))
      .addCase(resumeKioskTask.fulfilled, (state, a) => onTransitionFulfilled(state, a.payload))
      .addCase(resumeKioskTask.rejected, (state, a) => onTransitionRejected(state, "resume", a.payload))
      .addCase(doneKioskTask.pending, (state) => setActing(state, "done"))
      .addCase(doneKioskTask.fulfilled, (state, a) => onTransitionFulfilled(state, a.payload))
      .addCase(doneKioskTask.rejected, (state, a) => onTransitionRejected(state, "done", a.payload));

    builder
      .addCase(addKioskTaskNote.pending, (state) => {
        state.acting = "noteSubmit";
        state.actionError = undefined;
      })
      .addCase(addKioskTaskNote.fulfilled, (state) => {
        state.acting = undefined;
        state.noteCreated = new Date().toISOString();
      })
      .addCase(addKioskTaskNote.rejected, (state, a) => {
        state.acting = undefined;
        state.actionError = { action: "note", message: a.payload?.message, status: a.payload?.status ?? 0 };
      });
  },
});

export const { clearActionError, clearNoteCreated } = kioskTasksSlice.actions;
export default kioskTasksSlice.reducer;
